import { useFetchConnectedIntegrationAccounts } from "./useFetchConnectedIntegration";

import type { IntegrationScope } from "../constants";

interface UseGetLatestAccountIdProps {
  sourceId: string;
  selectedAccountId?: string;
  enabled?: boolean;
  integrationScope?: IntegrationScope;
}

/**
 * Generic hook to determine the account ID to use for integration operations.
 * Returns either the provided selectedAccountId or the latest connected account.
 *
 * @param sourceId - The source id (e.g., 'azure_storage', 'slack')
 * @param selectedAccountId - Pre-selected account ID (takes priority)
 * @param enabled - Whether to fetch connected accounts (default: true when no selectedAccountId)
 * @returns Object with accountIdToUse, connectedAccounts, and loading state
 */
export function useGetLatestAccountId({
  sourceId,
  selectedAccountId,
  enabled = !selectedAccountId,
  integrationScope,
}: UseGetLatestAccountIdProps) {
  // Fetch connected accounts when selectedAccountId is not provided
  const {
    data: connectedAccounts,
    isLoading: isFetchingAccounts,
    error: fetchError,
  } = useFetchConnectedIntegrationAccounts(sourceId, {
    enabled,
    integrationScope,
  });

  // Derive the account ID to use - either the provided one or the last connected account
  const accountIdToUse =
    selectedAccountId ||
    (connectedAccounts && connectedAccounts.length > 0
      ? connectedAccounts[connectedAccounts.length - 1].verifiable_id
      : "");

  return {
    accountIdToUse,
    connectedAccounts: connectedAccounts || [],
    isFetchingAccounts,
    fetchError,
  };
}
